import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useWebSocketContext } from "./WebSocketProvider";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN } from "constants/AuthConstant";
import dayjs from "dayjs";


const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const { lastMessage, sendMessage } = useWebSocketContext();

  const [dashboard, setDashboard] = useState([]);
  const [events, setEvents] = useState([]);
  const [mainboard, setMainboard] = useState([]);
  const [scrollboard, setScrollboard] = useState([]);
  const jwtToken = localStorage.getItem(AUTH_TOKEN);

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: jwtToken,
    },
  });
  // ✅ Fetch initial data from API when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, eventsRes, mainboardRes, scrollboardRes] =
          await Promise.all([
            api.get(`/dashboard`),
            api.get(`/events`),
            api.get(`/mainboard`),
            api.get(`/scrollboard`),
          ]);

        setDashboard(dashboardRes.data);
        setEvents(eventsRes.data);
        setMainboard(mainboardRes.data);
        setScrollboard(scrollboardRes.data);

        console.log("✅ Initial data loaded from API");
      } catch (error) {
        console.error("❌ Failed to fetch initial data:", error);
      }
    };

    fetchData();
  }, []);

  // ✅ Handle WebSocket Messages (ONLY for updates, not initial load)
  useEffect(() => {
    if (lastMessage) {
      try {
        const data = JSON.parse(lastMessage.data);

        const { type, action, payload = data } = data;
        console.log(" WebSocket:", payload);

       
        switch (type) {
          case "events":
            updateState(setEvents, action, payload.data);
            break;
          case "dashboard":
            updateState(setDashboard, action, payload.data);
            break;
          case "mainboard":
            updateState(setMainboard, action, payload.data);
            break;
          case "scrollboard":
            updateState(setScrollboard, action, payload.data);
            break;
          case "pong":
            console.log("pong received @ : " + dayjs().format("DD MMM YYYY hh:mm:ss"));
            break;
          case "auth":
            console.log(payload);
            break;
          default:
            console.warn("⚠️ Unknown WebSocket message type:", type);
        }
      } catch (error) {
        console.error("❌ WebSocket message parse error:", error);
      }
    }
  }, [lastMessage]);

  // Generic function to update state based on action type
  const updateState = (setState, action, payload) => {
    setState((prevData) => {
      switch (action) {
        case "create":
          return [...prevData, payload]; // Append new item
        case "update":
          return prevData.map((item) =>
            item.id == payload.id ? payload : item
          ); // Replace existing item
        case "delete":
          return prevData.filter((item) => item.id !== payload); // Remove item
        default:
          return prevData; // Keep previous state if action is unknown
      }
    });
   
  };

  // ✅ CRUD Operations with API Calls
  const addItem = async (type, item) => {
    try {
      const response = await api.post(`/${type}`, item);

      item.id = response.data.id;

      switch (type) {
        case "dashboard":
          setDashboard([...dashboard, item]);
          break;
        case "events":
          setEvents([...events, item]);
          break;
        case "mainboard":
          setMainboard([...mainboard, item]);
          break;
        case "scrollboard":
          setScrollboard([...scrollboard, item]);
          break;
        default:
          return;
      }

      sendMessage(JSON.stringify({ type, action: "create", data: item }));
    } catch (error) {
      console.error(`❌ Failed to add ${type}:`, error);
    }
  };

  const updateItem = async (type, id, updatedFields) => {
    try {
      const response = await api.put(`/${type}/${id}`, updatedFields);
    
      switch (type) {
        case "dashboard":
          setDashboard(
            dashboard.map((item) => (item.id == id ? updatedFields : item))
          );
         
          break;
        case "events":
          setEvents(
            events.map((item) => (item.id === id ? updatedFields : item))
          );
          break;
        case "mainboard":
          setMainboard(
            mainboard.map((item) => (item.id === id ? updatedFields : item))
          );
          break;
        case "scrollboard":
          setScrollboard(
            scrollboard.map((item) => (item.id === id ? updatedFields : item))
          );
          break;
        default:
          return;
      }
      
      sendMessage(
        JSON.stringify({type,  action: "update", data: updatedFields })
      );
    } catch (error) {
      console.error(`❌ Failed to update ${type}:`, error);
    }
  };

  const deleteItem = async (type, id) => {
    try {
      await api.delete(`/${type}/${id}`);

      switch (type) {
        case "events":
          setEvents(events.filter((item) => item.id !== id));
          break;
        case "mainboard":
          setMainboard(mainboard.filter((item) => item.id !== id));
          break;
        case "scrollboard":
          setScrollboard(scrollboard.filter((item) => item.id !== id));
          break;
        default:
          return;
      }

      sendMessage(JSON.stringify({ type, action: "delete", data: id }));
    } catch (error) {
      console.error(`❌ Failed to delete ${type}:`, error);
    }
  };

  return (
    <DataContext.Provider
      value={{
        dashboard,
        events,
        mainboard,
        scrollboard,
        addItem,
        updateItem,
        deleteItem,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => useContext(DataContext);
